// frontend/src/components/pages/admin/usage/UsageTab.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  getUsageByAll,
  getHotModels,
  getUsageByRole,
} from "../../../../api/apiFunction/usageServices";
import { FaCrown, FaMedal, FaStar, FaTrophy } from "react-icons/fa";
import NivoCharts from "../../../ui/NivoCharts";
import UsageStatsCards from "./UsageStatsCards";
import "../../../../styles/UsageCards.css";

const rankBadges = [
  {
    bg: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    icon: <FaCrown size={14} className="mr-1" />,
    label: "1st",
  },
  {
    bg: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-200",
    icon: <FaMedal size={14} className="mr-1" />,
    label: "2nd",
  },
  {
    bg: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-200",
    icon: <FaMedal size={14} className="mr-1" />,
    label: "3rd",
  },
  {
    bg: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-200",
    icon: <FaStar size={14} className="mr-1" />,
    label: "4th",
  },
  {
    bg: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-200",
    icon: <FaStar size={14} className="mr-1" />,
    label: "5th",
  },
];
const otherBadge = {
  bg: "bg-bg-40 text-fg-60 dark:bg-bg-80 dark:text-fg-30",
  icon: <FaStar size={14} className="mr-1" />,
};

const UsageTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hot models state
  const [hotModels, setHotModels] = useState([]);
  const [hotLoading, setHotLoading] = useState(true);
  const [hotError, setHotError] = useState(null);
  // New: role usage
  const [individualUsage, setIndividualUsage] = useState(null);
  const [organizationUsage, setOrganizationUsage] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [roleError, setRoleError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getUsageByAll()
      .then(setData)
      .catch((err) => setError(err?.error || "Failed to fetch overall usage"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setHotLoading(true);
    setHotError(null);
    getHotModels()
      .then((res) => setHotModels(res.models || []))
      .catch((err) => setHotError(err?.error || "Failed to fetch hot models"))
      .finally(() => setHotLoading(false));
  }, []);

  // Fetch both roles
  useEffect(() => {
    setRoleLoading(true);
    setRoleError(null);
    Promise.all([getUsageByRole("individual"), getUsageByRole("organization")])
      .then(([ind, org]) => {
        setIndividualUsage(ind);
        setOrganizationUsage(org);
      })
      .catch((err) => setRoleError(err?.error || "Failed to fetch role usage"))
      .finally(() => setRoleLoading(false));
  }, []);

  // Prepare data for the three bar charts using new API structure
  const allTokens =
    (data?.totals?.highEndTokens || 0) + (data?.totals?.smallTokens || 0);
  const orgTokens =
    (organizationUsage?.totals?.highEndTokens || 0) +
    (organizationUsage?.totals?.smallTokens || 0);
  const indTokens =
    (individualUsage?.totals?.highEndTokens || 0) +
    (individualUsage?.totals?.smallTokens || 0);
  const allCost = data?.totals?.totalCost || 0;
  const orgCost = organizationUsage?.totals?.totalCost || 0;
  const indCost = individualUsage?.totals?.totalCost || 0;
  const allRequests = data?.totals?.totalRequests || 0;
  const orgRequests = organizationUsage?.totals?.totalRequests || 0;
  const indRequests = individualUsage?.totals?.totalRequests || 0;

  const tokensData = useMemo(
    () => [
      { source: "All", value: allTokens },
      { source: "Organization", value: orgTokens },
      { source: "Individual", value: indTokens },
    ],
    [allTokens, orgTokens, indTokens]
  );

  const costData = useMemo(
    () => [
      { source: "All", value: allCost },
      { source: "Organization", value: orgCost },
      { source: "Individual", value: indCost },
    ],
    [allCost, orgCost, indCost]
  );

  const requestsData = useMemo(
    () => [
      { source: "All", value: allRequests },
      { source: "Organization", value: orgRequests },
      { source: "Individual", value: indRequests },
    ],
    [allRequests, orgRequests, indRequests]
  );

  // Color map for roles (light and dark theme)
  const colorMap = {
    All: "var(--color-all-bg)",
    Organization: "var(--color-org-bg)",
    Individual: "var(--color-ind-bg)",
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh] w-full">
        <span>Loading...</span>
      </div>
    );
  if (error)
    return (
      <div className="flex items-center justify-center h-[60vh] w-full text-red-500">
        {error}
      </div>
    );
  if (!data) return null;

  const stats = data.totals || {
    highEndTokens: 0,
    smallTokens: 0,
    totalCost: 0,
    totalRequests: 0,
  };
  const perDay = data.daily || [];
  const totalTokens = stats.highEndTokens + stats.smallTokens;
  const statValues = [totalTokens, stats.totalCost, stats.totalRequests];

  return (
    <div className="w-full flex justify-center p-6 h-full overflow-y-auto custom-scrollbar">
      <div className="max-w-7xl w-full">
        {/* Overall Usage Stats */}
        <UsageStatsCards
          stats={[
            {
              label: "All tokens",
              value: statValues[0],
              colorVar: "--usage-bg-01",
              fgVar: "--usage-fg-01",
              unit: "tokens",
            },
            {
              label: "Organization tokens",
              value: orgTokens,
              colorVar: "--usage-bg-02",
              fgVar: "--usage-fg-02",
              unit: "tokens",
            },
            {
              label: "Individual tokens",
              value: indTokens,
              colorVar: "--usage-bg-03",
              fgVar: "--usage-fg-03",
              unit: "tokens",
            },
          ]}
          title="Overall Usage Stats"
        />
        {/* Three Comparison Bar Charts */}
        <div className="mb-10">
          <h2 className="text-base font-semibold mb-4">Usage Comparison</h2>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Tokens Chart */}
            <div className="flex-1 mb-6" style={{ height: 320, minWidth: 0 }}>
              <h3 className="text-sm font-semibold mb-2">Tokens</h3>
              {roleLoading ? (
                <div className="text-fg-60 flex items-center justify-center h-full">
                  Loading...
                </div>
              ) : roleError ? (
                <div className="flex items-center justify-center h-full text-red-500">
                  {roleError}
                </div>
              ) : (
                <NivoCharts
                  data={tokensData}
                  keys={["value"]}
                  indexBy="source"
                  axisLeftLegend="Tokens"
                  colors={({ data }) => colorMap[data.source]}
                  enableLabel={false}
                />
              )}
            </div>
            {/* Cost Chart */}
            <div className="flex-1 mb-6" style={{ height: 320, minWidth: 0 }}>
              <h3 className="text-sm font-semibold mb-2">Cost</h3>
              {roleLoading ? (
                <div className="flex items-center justify-center h-full">
                  Loading...
                </div>
              ) : roleError ? (
                <div className="flex items-center justify-center h-full text-red-500">
                  {roleError}
                </div>
              ) : (
                <NivoCharts
                  data={costData}
                  keys={["value"]}
                  indexBy="source"
                  axisLeftLegend="Cost"
                  colors={({ data }) => colorMap[data.source]}
                  enableLabel={false}
                  unit={"$"}
                />
              )}
            </div>
            {/* Requests Chart */}
            <div className="flex-1 mb-6" style={{ height: 320, minWidth: 0 }}>
              <h3 className="text-sm font-semibold mb-2">Requests</h3>
              {roleLoading ? (
                <div className="flex items-center justify-center h-full">
                  Loading...
                </div>
              ) : roleError ? (
                <div className="flex items-center justify-center h-full text-red-500">
                  {roleError}
                </div>
              ) : (
                <NivoCharts
                  data={requestsData}
                  keys={["value"]}
                  indexBy="source"
                  axisLeftLegend="Requests"
                  colors={({ data }) => colorMap[data.source]}
                  enableLabel={false}
                />
              )}
            </div>
          </div>
        </div>
        {/* Hot Models Section */}
        <div className="mb-10">
          <h2 className="text-base font-semibold mb-2">Most Used Models</h2>
          <div className="rounded-xl bg-bg-60 overflow-hidden">
            {hotLoading ? (
              <div className="flex items-center justify-center h-24">
                Loading...
              </div>
            ) : hotError ? (
              <div className="flex items-center justify-center h-24 text-red-500">
                {hotError}
              </div>
            ) : hotModels.length > 0 ? (
              <table className="min-w-full divide-y divide-bd-50">
                <thead className="bg-bg-40">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                      Model Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                      Requests
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider"
                      style={{ minWidth: 120, width: 320 }}
                    >
                      Usage Bar
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const maxRequests = Math.max(
                      ...hotModels.map((m) => m.requestCount || 0),
                      1
                    );
                    return hotModels.map((model, idx) => {
                      const badge = idx < 5 ? rankBadges[idx] : otherBadge;
                      const place = idx + 1;
                      let placeSuffix = "th";
                      if (place % 10 === 1 && place % 100 !== 11)
                        placeSuffix = "st";
                      else if (place % 10 === 2 && place % 100 !== 12)
                        placeSuffix = "nd";
                      else if (place % 10 === 3 && place % 100 !== 13)
                        placeSuffix = "rd";
                      let label = "";
                      if (place === 1) label = "Winner";
                      else label = `${place}${placeSuffix} Place`;
                      const percent = Math.round(
                        ((model.requestCount || 0) / maxRequests) * 100
                      );
                      // Add border-bottom to all rows except the last
                      const isLast = idx === hotModels.length - 1;
                      return (
                        <tr
                          key={model._id}
                          className={`${
                            !isLast ? "border-b border-bd-50" : ""
                          } group`}
                        >
                          <td
                            className="px-6 py-2 whitespace-nowrap text-sm font-bold text-fg-50 bg-transparent"
                            style={{ minWidth: 160 }}
                          >
                            <span className="inline-flex items-center gap-1">
                              {/* 1st Place badge for top model */}
                              {idx === 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  <FaTrophy className="mr-1" size={12} />
                                  1st Place
                                </span>
                              )}
                              {/* Winner or other badge */}
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${badge.bg} min-w-[90px] justify-start`}
                                style={{ display: "inline-flex" }}
                              >
                                {badge.icon}
                                {label}
                              </span>
                            </span>
                          </td>
                          <td className="px-6 py-2 whitespace-nowrap text-sm text-fg-50 bg-transparent">
                            {model.name}
                          </td>
                          <td
                            className="px-6 py-2 whitespace-nowrap text-sm text-fg-50 bg-transparent"
                            style={{ minWidth: 80 }}
                          >
                            <span className="text-sm font-semibold text-fg-50 min-w-[32px] text-left">
                              {model.requestCount}
                            </span>
                          </td>
                          <td
                            className="px-6 py-2 whitespace-nowrap bg-transparent"
                            style={{ minWidth: 120, width: 180 }}
                          >
                            <div
                              className="my-1 h-6 rounded-lg transition-all"
                              style={{
                                width: `${percent}%`,
                                minWidth: 0,
                                opacity: 0.85,
                                background:
                                  "linear-gradient(90deg, transparent 0%, #4F8CFF 100%)",
                              }}
                            />
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            ) : (
              <div className="flex items-center justify-center h-24 text-fg-60">
                No model usage data found
              </div>
            )}
          </div>
        </div>
        {/* Daily Overall Stats */}
        <div className="mb-10">
          <h2 className="text-base font-semibold mb-2">Daily Overall Stats</h2>
          <div className="rounded-xl overflow-hidden">
            <table className="min-w-full divide-y divide-bd-50">
              <thead className="bg-bg-40">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Requests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Tokens
                  </th>
                </tr>
              </thead>
              <tbody>
                {perDay.length > 0 ? (
                  perDay.map((stat, idx) => (
                    <tr
                      key={idx}
                      className={
                        idx % 2 === 0
                          ? "bg-bg-60 dark:bg-bg-70"
                          : "bg-bg-50/40 dark:bg-bg-60/40"
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        {stat._id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        {stat.requests}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-50">
                        {(stat.highEndTokens || 0) + (stat.smallTokens || 0)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      className="px-6 py-4 text-center text-sm text-fg-60"
                    >
                      No daily stats found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="h-10" />
        </div>
      </div>
    </div>
  );
};

export default UsageTab;
